/*
 * AppProvider
 * @module features/App
 * @flow
 */
import React, { Fragment } from 'react';
// $FlowIssue
import { addLocaleData, IntlProvider } from 'react-intl';
// $FlowIssue
import en from 'react-intl/locale-data/en';
// $FlowIssue
import zh from 'react-intl/locale-data/zh';
import { createGlobalStyle, ThemeProvider } from 'styled-components/macro';
import reset from 'styled-reset';
import { key, font, palette } from 'styled-theme';
import * as R from 'ramda';

import translations from '@/i18n';
import * as themes from '@/themes';

export type AppProviderProps = $Shape<SettingsQuery> & {};

R.map(addLocaleData, [en, zh]);

const GlobalStyles = createGlobalStyle`
  ${reset}

  body {
    font-family: ${font('primary')};
    color: ${palette('grayscale', 1)};
  }

  h1, h2 {
    font-weight: 600;
    color: ${palette('grayscale', 0)};
  }

  h1 {
    font-size: ${key('fontSizes.h1')};
  }

  h2 {
    font-size: ${key('fontSizes.h2')};
  }

  p {
    font-weight: normal;
    font-size: ${key('fontSizes.p')};
    color: ${palette('grayscale', 1)};
  }

  a {
    color: ${palette(1)};
    text-decoration: none;
  }

  html, body, #root {
    height: 100%;
  }

  .rc-tooltip {
    && {
      /* NOTE: modal, search and tooltip must have the same z-index value but lower than notifications container */
      z-index: 999;
    }

    &.react-tooltip {
      width: 140px;
      font-size: 11px;
      overflow-wrap: break-word;
    }

    &.react-button-tooltip {
      width: 300px;
      font-size: 11px;
    }

    &.react-input-tooltip {
      width: 160px;
      font-size: 11px;
    }

    &.react-small-tooltip {
      width: auto;
    }

    .rc-tooltip-inner {
      min-height: auto;
      font-size: 12px;
    }

    @media (max-width: 450px) {
      .rc-tooltip-arrow {
        display: none;
      }
    }
  }

  .react-select__menu {
    z-index: 99 !important;
  }

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-appearance: none;
    margin: 0;
  }
`;

GlobalStyles.defaultProps = {
  palette: 'primary',
};

/**
 * AppProvider React-component
 * @memberof features/App
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @example <caption>Simple usage</caption>
 * <AppProvider><div /></AppProvider>
 * @returns {React.Node<*>} React-component
 */
export const AppProvider = ({ settings, ...rest }: AppProviderProps) => {
  // $FlowIssue
  const locale = R.propOr('en', 'locale', settings);
  const messages = R.propOr({}, locale, translations);

  const theme = R.pipe(
    R.prop('theme'),
    // $FlowIssue
    R.propOr('defaultTheme', R.__, themes), // eslint-disable-line no-underscore-dangle
  )(settings);

  return (
    <ThemeProvider theme={theme}>
      <Fragment>
        <GlobalStyles />
        <IntlProvider
          locale={locale}
          messages={messages}
          textComponent={Fragment}
          {...rest}
        />
      </Fragment>
    </ThemeProvider>
  );
};
